/**
 * RivalCard — the "Call someone out" opponent card (specs/challenge-flow-redesign — Story 1,
 * design "Challenge Flow Redesign.dc.html" 2a). A tall mascot card: a large, uncropped avatar
 * on a purple-wash panel with the name, an H2H record row (W/D/L count tiles), and coins below.
 * Presentational only — the grid's Pressable wrapper (FriendSelectorList) owns
 * press/selection/disabled/walkthrough wiring.
 *
 * The record is the aggregate head-to-head from the friends-list payload (wins/losses/totalChallenges);
 * the design's ordered form *sequence* + 🔥streak aren't in that payload, so we render counts in the
 * design's tile language (spec §Non-Goals / Q-1). The Monkey shows 0 coins (server sends null) so all
 * cards keep the same height.
 */

import React, { useMemo } from 'react';
import { View, Text } from 'react-native';
import { Image } from 'expo-image';
import { FriendAvatar } from '../../friends/shared/FriendAvatar';
import { getSafeDisplayName, isAvatarUrl } from '../../../src/create/helpers/createUi.helper';
import { getRivalRecord } from '../../../src/create/helpers/rivalRecord.helper';
import { MONKEY_USER_ID } from '../../../constants/monkey';
import { IDLE_COIN_SOURCE } from '../../../constants/assets';
import { WEB_IMAGE_ALT_DECORATIVE } from '../../../src/shared/helpers/imageWebAlt.helper';
import { useTheme } from '../../../src/theme/useTheme';
import type { Friend } from '../../../src/friends/types/friends.types';
import { getRivalCardStyles, getRivalCardDimensions, getRivalCardHeight } from './RivalCard.styles';

interface RivalCardProps {
  friend: Friend;
  cardWidth: number;
  isSelected: boolean;
  isDisabled?: boolean;
  testID?: string;
}

export const RivalCard: React.FC<RivalCardProps> = ({
  friend,
  cardWidth,
  isSelected,
  isDisabled = false,
  testID,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getRivalCardStyles(theme), [theme]);
  const { imageHeight, avatarSize } = useMemo(() => getRivalCardDimensions(cardWidth), [cardWidth]);
  const cardHeight = useMemo(() => getRivalCardHeight(cardWidth), [cardWidth]);

  const displayName = getSafeDisplayName(friend.name, friend.email);
  const record = useMemo(() => getRivalRecord(friend), [friend]);
  // Server sends coins:null for the Monkey (and the guest-synthetic Monkey carries a sentinel
  // balance) — normalise to 0 so its card height matches human cards (AC-1.3b).
  const coins = friend.id === MONKEY_USER_ID ? 0 : friend.coins ?? 0;

  return (
    <View
      style={[
        styles.card,
        { height: cardHeight },
        isSelected && styles.cardSelected,
        isDisabled && styles.cardDisabled,
      ]}
      testID={testID}
    >
      <View style={[styles.imageArea, { height: imageHeight }]}>
        <FriendAvatar
          avatarId={friend.avatarId ?? undefined}
          avatarConfig={friend.avatarConfig}
          fallbackUrl={isAvatarUrl(friend.avatar ?? '') ? friend.avatar : undefined}
          size={avatarSize}
          nameForInitials={friend.name}
          variant="home"
          avatarRounded={false}
        />
        {isSelected && (
          <View style={styles.selectedBadge}>
            <Text style={styles.selectedBadgeText}>✓</Text>
          </View>
        )}
      </View>

      <View style={styles.info}>
        <Text style={styles.name} numberOfLines={1}>
          {displayName}
        </Text>
        <View style={styles.metaRow}>
          {record.hasHistory ? (
            <View style={styles.recordRow}>
              <View style={[styles.recTile, styles.recWin]}>
                <Text style={styles.recTileText}>{record.wins}W</Text>
              </View>
              <View style={[styles.recTile, styles.recDraw]}>
                <Text style={styles.recTileText}>{record.draws}D</Text>
              </View>
              <View style={[styles.recTile, styles.recLoss]}>
                <Text style={styles.recTileText}>{record.losses}L</Text>
              </View>
            </View>
          ) : (
            <Text style={styles.noHistory}>First matchup</Text>
          )}
          <View style={styles.coinsRow}>
            <Image source={IDLE_COIN_SOURCE} style={styles.coinIcon} alt={WEB_IMAGE_ALT_DECORATIVE} />
            <Text style={styles.coinsText}>{coins.toLocaleString()}</Text>
          </View>
        </View>
      </View>
    </View>
  );
};
